import React, { useEffect, useState } from 'react';
import { Box, Title, Text, Card, Group, Badge, Button, Divider, Grid, ThemeIcon, ActionIcon, Tooltip } from '@mantine/core';
import { ExternalLink, LineChart, Plus, Minus, Equal, Star } from 'lucide-react';
interface Term {
  id: string;
  term: string;
  tag: string;
  summary: string;
  link: string;
  positive?: string[];
  neutral?: string[];
  negative?: string[];
}
interface TermCategory {
  id: string;
  name: string;
  terms: Term[];
}
export function ExploreTerminology() {
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const [starredTerms, setStarredTerms] = useState<{
    [key: string]: boolean;
  }>({});
  // Load starred terms from localStorage on component mount
  useEffect(() => {
    const savedExplores = localStorage.getItem('nutrivector-saved-explores');
    if (savedExplores) {
      const savedItems = JSON.parse(savedExplores);
      const starredMap = savedItems.reduce((acc, item) => {
        acc[`${item.category}-${item.id}`] = true;
        return acc;
      }, {});
      setStarredTerms(starredMap);
    }
  }, []);
  // Handler to star/unstar a term
  const handleStarTerm = (category: TermCategory, term: Term) => {
    const termKey = `${category.name}-${term.id}`;
    const isCurrentlyStarred = starredTerms[termKey];
    // Get existing saved explores from localStorage
    const existingSavedExplores = localStorage.getItem('nutrivector-saved-explores');
    let savedExplores = existingSavedExplores ? JSON.parse(existingSavedExplores) : [];
    if (isCurrentlyStarred) {
      // Remove from starred terms
      const newStarredTerms = {
        ...starredTerms
      };
      delete newStarredTerms[termKey];
      setStarredTerms(newStarredTerms);
      // Remove from localStorage
      savedExplores = savedExplores.filter(item => !(item.category === category.name && item.id === term.id));
    } else {
      // Add to starred terms
      setStarredTerms({
        ...starredTerms,
        [termKey]: true
      });
      // Add to localStorage
      const savedTerm = {
        id: term.id,
        term: term.term,
        tag: term.tag,
        category: category.name,
        summary: term.summary,
        link: term.link,
        positive: term.positive,
        neutral: term.neutral,
        negative: term.negative,
        savedAt: new Date().toISOString()
      };
      savedExplores.push(savedTerm);
    }
    // Save back to localStorage
    localStorage.setItem('nutrivector-saved-explores', JSON.stringify(savedExplores));
  };
  const termData: TermCategory[] = [{
    id: 'identity',
    name: 'Identity',
    terms: [{
      id: 'kosher',
      term: 'Kosher',
      tag: 'Kosher',
      summary: 'A kosher diet follows Jewish kashrut law: only certain animals (and their permitted parts) may be eaten, meat and dairy can never be served together, and slaughter must be carried out by a trained shochet. Separate utensils are kept for meat vs dairy, and many packaged foods carry a rabbinic seal to prove compliance. People choose it for cultural identity, ritual purity, and a built-in mindfulness about food sourcing.',
      link: 'http://www.healthline.com/nutrition/what-is-kosher'
    }, {
      id: 'halal',
      term: 'Halal',
      tag: 'Halal',
      summary: 'Halal eating aligns with Islamic dietary law. Pork, blood, and intoxicants (including alcohol) are forbidden, while lawful animals must be slaughtered with a single cut and a blessing (dhabiha). Meat and dairy may be mixed, but all ingredients—from gelatin to flavor extracts—must be verified halal. This framework supports ethical treatment of animals and spiritual consciousness at every meal.',
      link: 'http://www.healthline.com/nutrition/kosher-vs-halal'
    }, {
      id: 'ahimsa',
      term: 'Ahimsa',
      tag: 'Ahimsa (Jain)',
      summary: 'Rooted in the Jain principle of non-violence, the ahimsa diet is a strict lacto-vegetarian pattern that excludes eggs, meat, honey, and even root vegetables (onion, garlic, potato) to avoid harming microorganisms in soil. Many Jains filter water and avoid eating after dark. While high in fiber and antioxidants, careful planning is needed for vitamin B-12, iron, and protein.',
      link: 'http://www.healthline.com/nutrition/types-of-vegetarians'
    }, {
      id: 'diabetic',
      term: 'Diabetic',
      tag: 'Diabetic',
      summary: 'A diabetic diet moderates total carbohydrates, favors low-glycemic foods, and spaces meals evenly to blunt blood-sugar spikes. Emphasis is on whole grains, non-starchy vegetables, lean protein, healthy fats, and portion control. The goal is to improve A1C, prevent hypoglycemia, and support long-term insulin sensitivity.',
      link: 'http://www.healthline.com/health/type-2-diabetes-diet'
    }, {
      id: 'celiac',
      term: 'Celiac',
      tag: 'Celiac (Gluten-Free)',
      summary: 'In celiac disease, gluten triggers an autoimmune attack on the small-intestinal lining. The only treatment is lifelong elimination of wheat, barley, rye, and cross-contaminated foods. A nutrient-dense, naturally gluten-free menu (produce, legumes, meat, dairy, certified grains) heals villi, restores absorption, and lowers systemic inflammation.',
      link: 'http://www.healthline.com/nutrition/gluten-free-diet'
    }, {
      id: 'fodmap',
      term: 'FODMAP',
      tag: 'Low-FODMAP',
      summary: 'Designed for IBS relief, the low-FODMAP protocol removes fermentable short-chain carbs (fructans, lactose, polyols, etc.) for 2–6 weeks, then re-introduces them one at a time. This often reduces bloating, gas, and abdominal pain by lowering osmotic load and fermentation in the gut.',
      link: 'http://www.healthline.com/nutrition/fodmaps-101'
    }, {
      id: 'renal',
      term: 'Renal',
      tag: 'Renal',
      summary: 'For chronic kidney disease, a renal diet caps sodium, potassium, and phosphorus while moderating protein to reduce filtration stress. High-quality protein (egg whites, fish), low-sodium flavorings, and careful fluid management help maintain electrolyte balance and slow CKD progression.',
      link: 'http://www.healthline.com/nutrition/foods-to-avoid-with-kidney-disease'
    }, {
      id: 'antihistamine',
      term: 'Anti-histamine',
      tag: 'Histamine Intolerance',
      summary: "Those with DAO-enzyme deficits can't break down dietary histamine efficiently, leading to headaches, hives, and flushing. A low-histamine plan emphasizes fresh meat, produce, and gluten-free grains while avoiding aged cheese, fermented foods, cured meats, alcohol, and certain fish. Symptom relief often appears within days.",
      link: 'http://www.healthline.com/health/histamine-intolerance'
    }, {
      id: 'autoimmune',
      term: 'Autoimmune',
      tag: 'Autoimmune (AIP)',
      summary: "The Autoimmune Protocol is an elimination diet stricter than paleo: it removes grains, legumes, dairy, nuts, seeds, nightshades, alcohol, and additives for 30–90 days before systematic re-introduction. Early studies show reductions in IBD and Hashimoto's symptoms, likely by calming gut permeability and systemic inflammation.",
      link: 'http://www.healthline.com/nutrition/aip-diet-autoimmune-protocol-diet'
    }, {
      id: 'sattvic',
      term: 'Sattvic',
      tag: 'Sattvic',
      summary: 'A Sattvic diet, drawn from Ayurveda and yoga philosophy, focuses on "pure" foods—fresh fruits, vegetables, whole grains, legumes, mild spices, ghee, and honey—while excluding meat, eggs, alcohol, caffeine, onions, and garlic. Followers believe it promotes mental clarity, spiritual calm, and longevity, though it can be restrictive.',
      link: 'http://www.healthline.com/nutrition/sattvic-diet-review'
    }]
  }, {
    id: 'mealFrequency',
    name: 'Meal Frequency',
    terms: [{
      id: 'grazer',
      term: 'Grazer',
      tag: 'Grazer',
      summary: 'The grazing model spreads food across 5–6 mini-meals (every 2–3 hours). Advocates say it stabilizes blood sugar and supplies a steady amino-acid trickle for muscle repair—hence its popularity with bodybuilders and "always-on" professionals. Critics note that frequent eating can raise total caloric intake and keep insulin chronically elevated if portions aren\'t tightly managed.',
      link: 'https://www.healthline.com/nutrition/how-many-meals-per-day'
    }, {
      id: 'daystride',
      term: 'Daystride',
      tag: 'Daystride',
      summary: 'Daystride mirrors the classic "three square meals" pattern—breakfast, lunch, dinner—anchored to daylight hours. This cadence suits family and social schedules, aligns fairly well with circadian digestion research, and avoids the constant food prep of grazing while sidestepping the long fast of stricter protocols.',
      link: 'https://www.healthline.com/nutrition/how-many-meals-per-day'
    }, {
      id: 'sunfast',
      term: 'Sunfast',
      tag: 'Sunfast',
      summary: 'Sunfast is a 16:8 time-restricted-eating plan: fast for 16 hours (often overnight) and consume all calories in an 8-hour daylight window. Studies link this schedule to modest weight loss, improved insulin sensitivity, and cellular repair activation—all without strict calorie counting—though late-night shift workers may struggle to fit the daylight window.',
      link: 'https://www.healthline.com/nutrition/16-8-intermittent-fasting'
    }, {
      id: 'nightbound',
      term: 'Nightbound',
      tag: 'Nightbound',
      summary: "Nightbound (OMAD) compresses the entire day's intake into one evening meal after ~23 hours of fasting. Fans report simplicity, appetite resetting, and body-fat drops; research indicates metabolic benefits similar to other intermittent-fasting styles, but risks include nutrient shortfalls and energy crashes during extended fasts. It demands careful meal composition and may be unsuitable for diabetics or intense morning athletes.",
      link: 'https://www.healthline.com/health/one-meal-a-day'
    }]
  }, {
    id: 'foodType',
    name: 'Food Type',
    terms: [{
      id: 'beast',
      term: 'Beast',
      tag: 'Beast / Carnivore',
      summary: 'The carnivore diet restricts intake to animal products—red and white meat, fish, eggs, and sometimes dairy—eliminating plants entirely. Proponents cite rapid fat loss, autoimmune relief, and digestive calm (fewer fermentable fibers). The trade-offs: potential vitamin C, fiber, and phytonutrient gaps; elevated LDL in some individuals; and social dining hurdles. Strategic organ-meat inclusion and periodic labs are recommended to cover micronutrient bases.',
      link: 'https://www.healthline.com/nutrition/carnivore-diet'
    }, {
      id: 'tide',
      term: 'Tide',
      tag: 'Tide / Pescatarian',
      summary: 'A pescatarian pattern is plant-forward but includes fish and seafood for DHA/EPA omega-3s, iodine, and heme-like iron. It meshes the cardio benefits of Mediterranean eating with vegetarian greenhouse-gas ethics, and large cohort studies link it to lower heart-disease and diabetes risk. Key watch-outs are mercury in large predatory fish and adequate B-12 if dairy/eggs are minimal.',
      link: 'https://www.healthline.com/nutrition/pescatarian-diet'
    }, {
      id: 'plant',
      term: 'Plant',
      tag: 'Plant / Vegetarian',
      summary: 'Lacto-ovo vegetarianism removes meat and fish but allows dairy and eggs, broadening protein options compared with veganism. When planned around whole foods—legumes, nuts, seeds, produce—it delivers fiber, antioxidants, and lower LDL, while epidemiology shows reduced hypertension and obesity risk. Supplementing B-12 and balancing omega-6 to omega-3 fats are common tweaks.',
      link: 'https://www.healthline.com/nutrition/vegetarian-diet'
    }, {
      id: 'fusion',
      term: 'Fusion',
      tag: 'Fusion / Omnivore',
      summary: 'The omnivore—or "balanced plate"—approach draws from all food groups: vegetables, fruits, whole grains, legumes, dairy, eggs, seafood, and meat. Variety provides full amino-acid profiles, heme and non-heme iron, and diverse phytonutrients. Health outcomes hinge on food quality: whole-food omnivory (e.g., Mediterranean) supports longevity, whereas ultra-processed omnivory drives metabolic disease.',
      link: 'https://www.healthline.com/nutrition/balanced-diet'
    }]
  }, {
    id: 'macroStrategy',
    name: 'Macronutrient Strategy',
    terms: [{
      id: 'burn',
      term: 'Burn',
      tag: 'Burn / Low-Carb (High-Fat "Keto")',
      summary: 'The Burn motif skews macros to ~5-10% carbs, 65-75% fat, and the rest protein, pushing the body into nutritional ketosis. Benefits include rapid water-weight loss, appetite suppression, and therapeutic use in epilepsy and metabolic syndrome. Common pitfalls are "keto flu," micronutrient gaps, and LDL elevations; adding non-starchy veggies, electrolytes, and periodic carb refeeds can mitigate issues.',
      link: 'https://www.healthline.com/nutrition/ketogenic-diet-101'
    }, {
      id: 'surge',
      term: 'Surge',
      tag: 'Surge / Low-Fat (High-Carb)',
      summary: 'Surge flips the script—10-15% fat, 65-75% carbs—fueling glycogen stores for high-volume training while minimizing dietary fat for caloric room. Historically used by endurance athletes and old-school bodybuilders, it improves sprint repeatability and fast recovery but may leave fat-soluble vitamin intake low and hunger spikes if fiber is lacking.',
      link: 'https://www.healthline.com/nutrition/low-fat-diet'
    }, {
      id: 'flux',
      term: 'Flux',
      tag: 'Flux / Balanced',
      summary: 'Flux keeps macros near equilibrium (30-40% carbs, 30% protein, 30% fat) or flexes daily via IIFYM tracking. This middle path supports metabolic flexibility—switching between carb and fat oxidation—making it adaptable for most goals. Success hinges on portion control and micronutrient density rather than strict macro elimination.',
      link: 'https://www.healthline.com/nutrition/balanced-diet'
    }, {
      id: 'whisper',
      term: 'Whisper',
      tag: 'Whisper / Low-Protein',
      summary: 'Whisper drops protein below ~10% of calories (often via fruit-heavy or starchy plant menus) to lighten renal load, support spiritual/monastic practices, or test longevity theories around methionine restriction. While it boosts autophagy markers and digestive lightness, extended use risks sarcopenia, anemia, and immune compromise unless carefully cycled and supplemented.',
      link: 'https://www.healthline.com/nutrition/low-protein-diet-1'
    }]
  }, {
    id: 'feedingCycle',
    name: 'Feeding Cycle',
    terms: [{
      id: '180',
      term: '+/-',
      tag: 'Seasonal Bulk-Cut (180)',
      summary: 'The classic bodybuilding rhythm alternates long "bulk" phases (≈ 6 months of calorie surplus for muscle and strength) with equal-length "cuts" (≈ 6 months of mild deficit to peel off fat). This mirrors ancestral feast-or-lean patterns and lets hormones, connective tissue, and joints recover before the next push. Drawbacks: long bulks can add visceral fat; prolonged cuts risk muscle loss without high protein and resistance training.',
      link: 'https://www.bodybuilding.com/content/the-bodybuilders-guide-to-bulking-and-cutting.html'
    }, {
      id: '3.4',
      term: '3/4',
      tag: 'Weekly Carb Cycle (3/4)',
      summary: "Carb cycling varies carbohydrate intake across the week—typically 3–4 high-carb training days and 3–4 low-carb recovery days. High days refill glycogen and boost leptin; low days ramp up fat oxidation and insulin sensitivity. Athletes appreciate the performance–leaning balance, but the plan requires careful tracking and can tempt binge behavior if macro targets aren't clear.",
      link: 'https://www.healthline.com/nutrition/carb-cycling'
    }, {
      id: '6.1',
      term: '6/1',
      tag: 'Weekly Calorie Cycle (6/1)',
      summary: 'Also called a refeed schedule, six maintenance/deficit days are followed by one higher-calorie "refuel." The surge elevates leptin, thyroid output, and gym morale while giving dieters a psychological break. Effective for fat-loss plateaus, but overeating easily erases the week\'s deficit unless high-calorie food choices stay nutrient-dense.',
      link: 'https://www.healthline.com/nutrition/cheat-meals'
    }, {
      id: '2.5',
      term: '2/5',
      tag: 'Cyclic Keto (2/5)',
      summary: "The Cyclical Ketogenic Diet keeps you in strict keto (≤ 50 g carbs) for five days, then adds two carb-loading days to restore glycogen and training intensity. Because ketone adaptation resets quickly, this hybrid lets lifters lift heavy without abandoning keto's appetite and neuro benefits. It does, however, require disciplined transitions and may cause GI distress on carb-up days.",
      link: 'https://www.bodybuilding.com/content/your-complete-guide-to-the-cyclical-ketogenic-diet.html'
    }, {
      id: '52',
      term: '5/2',
      tag: '5:2 Fast Split (5/2)',
      summary: 'Popularized by Dr. Michael Mosley, the 5:2 method features five normal-calorie days and two non-consecutive very-low-calorie (≈ 500-600 kcal) "fast" days. Research shows modest weight loss, improved insulin sensitivity, and easier adherence compared with daily restriction. Some feel fatigued on fast days, and athletes may under-recover if high-output sessions fall on low-cal days.',
      link: 'https://www.healthline.com/nutrition/5-2-diet'
    }, {
      id: '7.0',
      term: '7/0',
      tag: 'Steady-State (7/0)',
      summary: 'A steady-state feeder consumes the same macros and calories every day, keeping hormones, digestion, and circadian rhythm locked into a predictable groove—ideal for habit-driven lifestyles and objective progress tracking. While simplicity aids compliance, the body can adapt metabolically; periodic assessments are needed to adjust intake when goals or activity change.',
      link: 'https://www.bodybuilding.com/content/meal-timing-and-frequency.html'
    }]
  }, {
    id: 'microStrategy',
    name: 'Micronutrient Strategy',
    terms: [{
      id: 'spice',
      term: 'Spice',
      tag: 'Spice',
      summary: 'The Spice strategy layers meals with antioxidant-rich herbs, roots, and seeds—turmeric, ginger, cumin, garlic, cinnamon, fenugreek, and chilies. These botanicals supply trace minerals, polyphenols, and anti-inflammatory compounds that boost gut diversity, improve glucose handling, and enhance immunity without adding calories. Cultures from India to Ethiopia have leveraged spice synergy for centuries, and modern studies confirm their cardiometabolic punch.',
      link: 'https://www.healthline.com/nutrition/10-healthy-herbs-and-spices'
    }, {
      id: 'brew',
      term: 'Brew',
      tag: 'Brew',
      summary: 'Brew focuses on nutrient extraction through teas, tisanes, broths, and decoctions. Green, black, and oolong teas deliver catechins and theaflavins; bone or vegetable broths supply collagen peptides and electrolytes; herbal infusions (ginger, chamomile, rooibos) add bioactive terpenes. Because liquids are rapidly absorbed, Brew offers gentle mineral repletion, gut soothe, and hydration—popular from British tea culture to Chinese gong-fu ceremonies.',
      link: 'https://www.healthline.com/nutrition/tea-benefits'
    }, {
      id: 'culture',
      term: 'Culture',
      tag: 'Culture',
      summary: 'Culture relies on fermented foods—kimchi, sauerkraut, kefir, miso, natto, yogurt—to seed beneficial bacteria, create B-vitamins (especially K₂ and folate), and pre-digest troublesome antinutrients. Regular intake improves microbiome diversity, reduces systemic inflammation, and enhances nutrient absorption. Korean "Hansik," Japanese temple cuisine, and Eastern-European pickling traditions all thrive on this microbial boost.',
      link: 'https://www.healthline.com/nutrition/fermented-foods'
    }, {
      id: 'marrow',
      term: 'Marrow',
      tag: 'Marrow',
      summary: 'The Marrow approach takes a nose-to-tail view: liver, heart, kidney, bone marrow, and long-simmered bone broth provide heme iron, retinol, B-12, choline, collagen, and rare cofactors like MK-4 (vitamin K₂). Indigenous Arctic and Plains peoples thrived on these dense staples when produce was scarce. Modern adopters use small organ servings weekly to cover micronutrient gaps impossible to meet with muscle meat alone.',
      link: 'https://www.healthline.com/nutrition/organ-meats'
    }, {
      id: 'elixir',
      term: 'Elixir',
      tag: 'Elixir',
      summary: 'Elixir leverages modern nutraceuticals—vitamin/mineral complexes, omega-3 concentrates, adaptogen powders, nootropic stacks, IV micronutrient drips—to target specific deficiencies or performance goals. It offers precision and convenience, but quality control, bioavailability, and "more is better" thinking are constant concerns; periodic labs guide safe, effective dosing.',
      link: 'https://www.healthline.com/nutrition/dietary-supplements'
    }, {
      id: 'alloy',
      term: 'Alloy',
      tag: 'Alloy',
      summary: 'Alloy fortifies the baseline diet with industrially enriched staples: iodized salt, folic-acid bread, vitamin-D milk, calcium-boosted plant milks, iron-fortified cereals, and electrolyte beverages. This public-health model successfully curbed goiter, rickets, and neural-tube defects in the 20th century, yet can create imbalances if over-relied upon or paired with ultra-processed fare.',
      link: 'https://www.healthline.com/nutrition/fortified-foods'
    }, {
      id: 'super',
      term: 'Super',
      tag: 'Super',
      summary: 'The Superfood strategy sprinkles small, potent hits of nutrient-dense items—spirulina, chlorella, cacao nibs, bee pollen, camu camu, acai, liver capsules—into otherwise balanced meals. Each "micro-dose" packs outsized vitamins, minerals, or phytonutrients, letting users top off antioxidant capacity without huge calorie loads. Variety is key; no single superfood is a panacea.',
      link: 'https://www.healthline.com/nutrition/16-superfoods'
    }]
  }, {
    id: 'exerciseTiming',
    name: 'Exercise-eating Timing',
    terms: [{
      id: 'emp',
      term: 'EMP',
      tag: 'EMP (Empty-State)',
      summary: 'Empty-State training means exercising after an overnight or 3-to-12-hour fast, with only water/electrolytes on board. Lifting or cardio in this low-insulin window may accelerate fat oxidation and improve metabolic flexibility, though some athletes report reduced peak power. Adequate post-workout protein and carbs are crucial to protect lean tissue and replenish glycogen.',
      link: 'https://www.healthline.com/health/fitness/benefits-of-fasted-cardio'
    }, {
      id: 'prm',
      term: 'PRM',
      tag: 'PRM (Primed-State)',
      summary: 'Primed-State calls for a small mixed snack—about 20 g protein plus 30–60 g fast-digesting carbs—60-90 minutes before training. This "top-off" stabilizes blood glucose, spares muscle glycogen, and boosts power output without the heaviness of a full meal. It\'s the sweet spot for most strength and hypertrophy programs.',
      link: 'https://www.healthline.com/nutrition/eat-before-workout'
    }, {
      id: 'fed',
      term: 'FED',
      tag: 'FED (Fed-State)',
      summary: 'In Fed-State timing, a full balanced meal (protein, starch, produce) is eaten within an hour of training. Bodybuilders and power athletes exploit the insulin surge to drive amino acids and creatine into muscle, maximize glycogen, and lift heavier loads—but the trade-off is potential GI discomfort if the session starts too soon after eating.',
      link: 'https://www.healthline.com/nutrition/eating-before-or-after-workout'
    }, {
      id: 'pst',
      term: 'PST',
      tag: 'PST (Post-Fed)',
      summary: 'Post-Fed emphasizes the recovery window: a protein-plus-carb meal or shake consumed immediately (within 30–60 min) after exercise. This strategy spikes muscle protein synthesis, attenuates soreness, and restocks glycogen—especially important after long endurance bouts or two-a-day sessions.',
      link: 'https://www.healthline.com/nutrition/eat-after-workout'
    }, {
      id: 'ref',
      term: 'REF',
      tag: 'REF (Delayed-Refeed)',
      summary: 'Delayed-Refeed delays the major meal 1–3 hours post-exercise, extending the natural rise in growth hormone while still delivering calories before bed. Weekly or bi-weekly "refeed" days then raise carb intake sharply to reset leptin, restore thyroid output, and break fat-loss plateaus.',
      link: 'https://www.healthline.com/nutrition/refeed-day'
    }, {
      id: 'bke',
      term: 'BKE',
      tag: 'BKE (Bookend Eating)',
      summary: 'Bookend Eating confines calories to the two edges of a workout—one meal or shake 30–60 min pre-session and one immediately after—leaving the rest of the day fasted or very light. This compresses digestion into a short window, improves nutrient partitioning, and simplifies meal prep for busy professionals.',
      link: 'https://www.bodybuilding.com/content/a-beginners-guide-to-workout-nutrition-before-during-and-after-training.html'
    }, {
      id: 'win',
      term: 'WIN',
      tag: 'WIN (Exer-Fed Window)',
      summary: "Exer-Fed Window widens Bookend into a 3-to-5-hour feeding block that starts shortly before training and ends a bit after. All day's calories cluster around the workout, exploiting heightened insulin sensitivity and shuttling nutrients straight to muscle while keeping the rest of the day a quasi-fasted metabolic state.",
      link: 'https://shop.bodybuilding.com/blogs/nutrition/shattering-the-anabolic-window'
    }]
  }, {
    id: 'radarHealth',
    name: 'Radar Health Categories',
    terms: [{
      id: 'athletics',
      term: 'Athletics',
      tag: 'Performance',
      summary: 'Explosive training performance thrives on ample substrate (carbs or ketones), generous protein, and tight workout-meal timing.',
      positive: ['Grazer (steady glycogen supply for repeated effort)', 'Surge (high-carb for performance)', '3/4 Carb-Cycle (strategic high-carb days)', 'FED / BKE timing plans that bracket lifting sessions with fuel'],
      neutral: ['Flux (balanced macros)', 'Sunfast (16:8 IF) with adequate protein and peri-workout carbs'],
      negative: ['Whisper (low protein)', 'Strict Nightbound OMAD', 'Medically constrained Renal diets'],
      link: 'https://www.healthline.com/nutrition/carb-cycling'
    }, {
      id: 'bodycomp',
      term: 'BodyComp',
      tag: 'Body Composition',
      summary: 'Maximal muscle with minimal fat demands high biologic-value protein, calorie cycling, and resistance-exercise meal support.',
      positive: ['Burn (keto cuts shed fat while sparing muscle)', '180 Bulk-Cut (dedicated hypertrophy and shredding phases)', 'PRM / PST workout feeding', 'Carnivore (Beast) for its dense amino profile'],
      neutral: ['Daystride with proper calorie control', 'Flux when calories match expenditure'],
      negative: ['Whisper (insufficient protein)', 'Low-FODMAP (often low protein/fiber)', 'Constant Surge high-carb without portion control'],
      link: 'https://www.bodybuilding.com/content/the-bodybuilders-guide-to-bulking-and-cutting.html'
    }, {
      id: 'metabolism',
      term: 'Metabolism',
      tag: 'Metabolic Health',
      summary: 'Efficient glucose and lipid handling correlates with balanced macros, meal timing, and anti-inflammatory micros.',
      positive: ['Burn (improves insulin sensitivity)', 'Sunfast (time-restricted eating)', 'Diabetic carb-moderation', 'EMP fasted training', 'Spice and Brew micronutrient layers'],
      neutral: ['Flux (balanced macros)', 'Plant (Vegetarian) with proper portioning and activity'],
      negative: ['Surge un-cycled (oversupply of simple carbs without activity)', 'Whisper (prolonged low-protein)', 'Nutrient-restricted plans affecting thyroid function'],
      link: 'https://www.healthline.com/nutrition/16-8-intermittent-fasting'
    }, {
      id: 'longevity',
      term: 'Longevity',
      tag: 'Health Span',
      summary: 'Cellular repair favors periodic caloric restraint, antioxidant density, and low chronic inflammation.',
      positive: ['5:2 Fast-Split', 'Sunfast (time-restricted eating)', 'Spice (antioxidant-rich herbs and spices)', 'Culture (fermented foods)', 'Pescatarian Tide', 'Autoimmune Protocol (AIP)'],
      neutral: ['Balanced omnivory', 'Moderate carb cycling', 'Brew tea traditions'],
      negative: ['Constant bulking (180 bulk months)', 'Ultra-processed Alloy reliance', 'Extreme high-fat carnivore without plants'],
      link: 'https://www.healthline.com/nutrition/5-2-diet'
    }, {
      id: 'wellbeing',
      term: 'Wellbeing',
      tag: 'Mental Resilience',
      summary: 'Energy, mood, and stress resilience rise with stable blood sugar, micronutrient sufficiency, and gut-brain harmony.',
      positive: ['Flux (balanced macros)', 'Brew (L-theanine & polyphenols)', 'Culture ferments', 'PRM pre-workout fueling', 'Sattvic eating (mindfulness-aligned)'],
      neutral: ['Kosher or Halal (wellbeing depends on food quality, not rules alone)'],
      negative: ['Constant grazing (Grazer)', 'All-day stimulants causing fatigue swings'],
      link: 'https://www.healthline.com/nutrition/fermented-foods'
    }, {
      id: 'ease',
      term: 'Ease',
      tag: 'Practicality',
      summary: 'Plans win on Ease when they are simple to shop, prep, and integrate socially.',
      positive: ['Daystride (three square meals)', 'Flux (balanced approach)', 'Fortified-food Alloy', 'Steady 7/0 (minimal tracking, mainstream foods)'],
      neutral: ['3/4 Carb-Cycle (requires tracking)', 'Marrow organ focus (exotic sourcing)', 'Supplement-heavy Elixir'],
      negative: ['Kosher kitchen separation', 'Ahimsa root avoidance', 'Nightly OMAD meal prep (labor-intensive)'],
      link: 'https://www.healthline.com/nutrition/balanced-diet'
    }, {
      id: 'immunity',
      term: 'Immunity',
      tag: 'Immune Resilience',
      summary: 'Protocols that lower inflammation and feed gut-immune crosstalk rate high.',
      positive: ['Culture (fermented foods)', 'Spice (anti-inflammatory herbs)', 'Autoimmune Protocol (AIP)', 'Omega-3-rich Tide (pescatarian)', 'Intermittent fasting (5:2)'],
      neutral: ['Carnivore (depends on micronutrient balance)', 'Renal (variable based on implementation)'],
      negative: ['Histamine-rich ferments for Histamine-Intolerance cases'],
      link: 'https://www.healthline.com/nutrition/aip-diet-autoimmune-protocol-diet'
    }, {
      id: 'digestion',
      term: 'Digestion',
      tag: 'Gut Health',
      summary: 'Fiber diversity, fermented foods, and FODMAP management govern gut comfort.',
      positive: ['Low-FODMAP for IBS relief', 'Culture (fermented foods)', 'Vegetarian Plant (fiber-rich)', 'Balanced Flux'],
      neutral: ['Keto with adequate vegetables', 'Pescatarian with moderate fiber'],
      negative: ['High-fat OMAD (Nightbound Beastburn)', 'Constant grazing without fiber (can cause bloating or constipation)'],
      link: 'https://www.healthline.com/nutrition/fodmaps-101'
    }, {
      id: 'hormones',
      term: 'Hormones',
      tag: 'Hormonal Balance',
      summary: 'Optimal endocrine profiles need adequate protein, essential fats, micronutrient cofactors, and circadian-matched meals.',
      positive: ['Burn (raises HDL & improves insulin sensitivity)', 'PST recovery feeding', 'Marrow nutrient density', 'Carb refeeds (6/1) to maintain leptin'],
      neutral: ['Balanced Flux', 'Vegetarian Plant with adequate fats'],
      negative: ['Low-protein Whisper', 'Chronic low-fat Surge (can depress sex hormones)'],
      link: 'https://www.healthline.com/nutrition/ketogenic-diet-101'
    }, {
      id: 'cognition',
      term: 'Cognition',
      tag: 'Brain Function',
      summary: 'Brain health benefits from stable glucose, omega-3s, ketones, polyphenols, and choline.',
      positive: ['Ketogenic Burn', 'Tea-rich Brew', 'Fasted-training EMP'],
      neutral: ['Balanced omnivore diet', 'Pescatarian with adequate DHA intake'],
      negative: ['High-GI low-fat surges', 'Nutrient-poor elimination extremes'],
      link: 'https://www.healthline.com/nutrition/tea-benefits'
    }, {
      id: 'culturefit',
      term: 'CultureFit',
      tag: 'Cultural Alignment',
      summary: 'Protocols that overlap multiple identity rules earn high marks for cultural adaptability.',
      positive: ['Plant/Vegetarian (meets Kosher, Halal, and FODMAP basics)', 'Low-FODMAP (adaptable to many cultural needs)', 'Flux balanced omnivory (can be tweaked for most cultures)'],
      neutral: ['Technical workout-timing schemes (WIN, EMP) are culturally agnostic'],
      negative: ['Carnivore (Beast) conflicts with vegetarian/ahimsa and many religious norms'],
      link: 'https://www.healthline.com/nutrition/vegetarian-diet'
    }, {
      id: 'satiety',
      term: 'Satiety',
      tag: 'Hunger Control',
      summary: 'High protein, fiber, water, and meal-timing finesse curb hunger.',
      positive: ['Burn (ketones suppress appetite)', 'Flux with protein ≥30%', 'Sunfast (compressed eating window)', 'Marrow protein–fat satiety'],
      neutral: ['Vegetarian Plant if protein and fiber are sufficient'],
      negative: ['Grazing (frequent eating patterns)', 'Low-protein Whisper (drives frequent hunger)'],
      link: 'https://www.healthline.com/nutrition/ketogenic-diet-101'
    }]
  }];
  const handleCategoryClick = (categoryId: string) => {
    setActiveCategory(activeCategory === categoryId ? null : categoryId);
  };
  const renderCategoryButton = category => {
    if (category.id === 'radarHealth') {
      return <Button key={category.id} variant={activeCategory === category.id ? 'filled' : 'outline'} color={activeCategory === category.id ? 'emerald' : 'gray'} onClick={() => handleCategoryClick(category.id)} className="mb-2" styles={{
        root: {
          backgroundColor: activeCategory === category.id ? '#10b981' : '#4b5563',
          color: 'white',
          '&:hover': {
            backgroundColor: activeCategory === category.id ? '#059669' : '#374151'
          }
        }
      }} data-id="element-130">
          {category.name}
        </Button>;
    }
    return <Button key={category.id} variant={activeCategory === category.id ? 'filled' : 'outline'} color={activeCategory === category.id ? 'emerald' : 'gray'} onClick={() => handleCategoryClick(category.id)} className="mb-2" data-id="element-131">
        {category.name}
      </Button>;
  };
  const renderRadarHealthCard = (category, term) => {
    const termKey = `${category.name}-${term.id}`;
    const isStarred = starredTerms[termKey] || false;
    return <Card key={term.id} shadow="sm" p="lg" radius="md" className="mb-6 bg-slate-50" data-id="element-132">
        <Group position="apart" className="mb-2" data-id="element-133">
          <Title order={4} className="text-slate-800" data-id="element-134">
            {term.term}
          </Title>
          <Group spacing="xs" data-id="element-135">
            <Badge color="emerald" size="lg" data-id="element-136">
              {term.tag}
            </Badge>
            <Tooltip label={isStarred ? 'Remove from saved' : 'Save for later'} data-id="element-137">
              <ActionIcon color={isStarred ? 'yellow' : 'gray'} variant={isStarred ? 'filled' : 'subtle'} onClick={() => handleStarTerm(category, term)} data-id="element-138">
                <Star size={16} fill={isStarred ? 'currentColor' : 'none'} data-id="element-139" />
              </ActionIcon>
            </Tooltip>
          </Group>
        </Group>
        <Divider className="my-3" data-id="element-140" />
        <Text className="text-slate-700 mb-4 font-medium" data-id="element-141">{term.summary}</Text>
        <Grid className="mb-4" data-id="element-142">
          <Grid.Col span={12} md={4} data-id="element-143">
            <div className="bg-emerald-50 p-4 rounded-md h-full" data-id="element-144">
              <Group className="mb-2" data-id="element-145">
                <ThemeIcon color="green" variant="light" size="md" radius="xl" data-id="element-146">
                  <Plus size={16} data-id="element-147" />
                </ThemeIcon>
                <Text fw={600} className="text-emerald-700" data-id="element-148">
                  Positive Impact (+1)
                </Text>
              </Group>
              {term.positive && term.positive.length > 0 ? <ul className="list-disc pl-5 text-sm text-slate-700 space-y-1" data-id="element-149">
                  {term.positive.map((item, idx) => <li key={idx} data-id="element-150">{item}</li>)}
                </ul> : <Text size="sm" className="text-slate-600" data-id="element-151">
                  No specific positive protocols listed.
                </Text>}
            </div>
          </Grid.Col>
          <Grid.Col span={12} md={4} data-id="element-152">
            <div className="bg-slate-100 p-4 rounded-md h-full" data-id="element-153">
              <Group className="mb-2" data-id="element-154">
                <ThemeIcon color="gray" variant="light" size="md" radius="xl" data-id="element-155">
                  <Equal size={16} data-id="element-156" />
                </ThemeIcon>
                <Text fw={600} className="text-slate-700" data-id="element-157">
                  Neutral Impact (0)
                </Text>
              </Group>
              {term.neutral && term.neutral.length > 0 ? <ul className="list-disc pl-5 text-sm text-slate-700 space-y-1" data-id="element-158">
                  {term.neutral.map((item, idx) => <li key={idx} data-id="element-159">{item}</li>)}
                </ul> : <Text size="sm" className="text-slate-600" data-id="element-160">
                  No specific neutral protocols listed.
                </Text>}
            </div>
          </Grid.Col>
          <Grid.Col span={12} md={4} data-id="element-161">
            <div className="bg-red-50 p-4 rounded-md h-full" data-id="element-162">
              <Group className="mb-2" data-id="element-163">
                <ThemeIcon color="red" variant="light" size="md" radius="xl" data-id="element-164">
                  <Minus size={16} data-id="element-165" />
                </ThemeIcon>
                <Text fw={600} className="text-red-700" data-id="element-166">
                  Negative Impact (-1)
                </Text>
              </Group>
              {term.negative && term.negative.length > 0 ? <ul className="list-disc pl-5 text-sm text-slate-700 space-y-1" data-id="element-167">
                  {term.negative.map((item, idx) => <li key={idx} data-id="element-168">{item}</li>)}
                </ul> : <Text size="sm" className="text-slate-600" data-id="element-169">
                  No specific negative protocols listed.
                </Text>}
            </div>
          </Grid.Col>
        </Grid>
        <Button component="a" href={term.link} target="_blank" rel="noopener noreferrer" variant="outline" color="emerald" rightIcon={<ExternalLink size={16} data-id="element-171" />} size="sm" data-id="element-170">
          Learn More
        </Button>
      </Card>;
  };
  const renderStandardCard = (category, term) => {
    const termKey = `${category.name}-${term.id}`;
    const isStarred = starredTerms[termKey] || false;
    return <Card key={term.id} shadow="sm" p="lg" radius="md" className="mb-4" data-id="element-172">
        <Group position="apart" className="mb-2" data-id="element-173">
          <Title order={4} className="text-slate-800" data-id="element-174">
            {term.term}
          </Title>
          <Group spacing="xs" data-id="element-175">
            <Badge color="emerald" size="lg" data-id="element-176">
              {term.tag}
            </Badge>
            <Tooltip label={isStarred ? 'Remove from saved' : 'Save for later'} data-id="element-177">
              <ActionIcon color={isStarred ? 'yellow' : 'gray'} variant={isStarred ? 'filled' : 'subtle'} onClick={() => handleStarTerm(category, term)} data-id="element-178">
                <Star size={16} fill={isStarred ? 'currentColor' : 'none'} data-id="element-179" />
              </ActionIcon>
            </Tooltip>
          </Group>
        </Group>
        <Divider className="my-3" data-id="element-180" />
        <Text className="text-slate-700 mb-4" data-id="element-181">{term.summary}</Text>
        <Button component="a" href={term.link} target="_blank" rel="noopener noreferrer" variant="outline" color="emerald" rightIcon={<ExternalLink size={16} data-id="element-183" />} size="sm" data-id="element-182">
          Learn More
        </Button>
      </Card>;
  };
  return <Box className="p-8 bg-white rounded-lg shadow-sm" data-id="element-184">
      <Title order={2} className="mb-4 text-slate-800" data-id="element-185">
        Nutrivector Terminology Guide
      </Title>
      <Text className="text-slate-600 mb-6" data-id="element-186">
        Explore the comprehensive terminology used in Nutrivector to better
        understand the different nutrition protocols and approaches. Click on
        each category to learn more about specific terms.
      </Text>
      <div className="mb-6 flex flex-wrap gap-2" data-id="element-187">
        {termData.map(category => renderCategoryButton(category))}
      </div>
      {termData.map(category => <div key={category.id} className={`mb-8 ${activeCategory !== null && activeCategory !== category.id ? 'hidden' : ''}`} data-id="element-188">
          <Group position="apart" className="mb-4" data-id="element-189">
            <Title order={3} className="text-emerald-700" data-id="element-190">
              {category.name}
            </Title>
            {category.id === 'radarHealth' && <Badge color="emerald" size="lg" className="flex items-center gap-1" data-id="element-191">
                <LineChart size={14} data-id="element-192" /> Radar Chart Dimensions
              </Badge>}
          </Group>
          <div className="space-y-4" data-id="element-193">
            {category.terms.map(term => category.id === 'radarHealth' ? renderRadarHealthCard(category, term) : renderStandardCard(category, term))}
          </div>
        </div>)}
    </Box>;
}